<script setup lang="ts">
import type { NuxtLinkProps } from '#app'
import type { EnrichedSearchResult } from '~/utils/solr/search'
import { faLockKeyhole } from '@fortawesome/pro-light-svg-icons'
import { TRUNCATE_LENGTH_DESKTOP, TRUNCATE_LENGTH_MOBILE } from '~/constants/search'
import {
  getSearchResultAccessibleLabel,
  getSearchResultTitleContext,
  nodeTypeToLabels,
  resolveSearchResult,
} from '~/utils/solr/search-utils'
import { truncateText } from '~/utils/string'

const props = defineProps<
  NuxtLinkProps & {
    result: EnrichedSearchResult
  }
>()

const emit = defineEmits<{
  clickResult: [result: EnrichedSearchResult]
}>()

const hasLicense = useHasLicense()

const JOURNAL_NODE_TYPES = [
  'journal_article',
  'journal_editorial',
  'journal_section',
  'journal_colophon',
]
const DESKTOP_WIDTH = 1024

const { width } = useWindowSize()
const isDesktop = computed(() => width.value >= DESKTOP_WIDTH)
const maxLength = computed(() =>
  isDesktop.value ? TRUNCATE_LENGTH_DESKTOP : TRUNCATE_LENGTH_MOBILE,
)
const journalEditionInfo = computed(() => {
  if (!JOURNAL_NODE_TYPES.includes(props.result.nodeType ?? '')) {
    return null
  }
  const parts = []
  if (props.result.volume) {
    parts.push(`Volum ${props.result.volume}`)
  }
  if (props.result.issue) {
    parts.push(`Utgave  ${props.result.issue}`)
  }
  return parts.length ? parts.join(' / ') : null
})

const truncatedBodyText = computed(() => {
  const bodyText = normalizeLineBreaks(props.result?.highlightedBody)
  const { truncated } = truncateText(bodyText, maxLength.value)
  return truncated
})

const formattedDate = computed(() => {
  if (!props.result.publishedAt) {
    return ''
  }

  return formatDate(props.result.publishedAt, { year: 'numeric', month: 'long', day: 'numeric' })
})

const isBook = computed(() => props.result.nodeType === 'book')
const isJournal = computed(() => JOURNAL_NODE_TYPES.includes(props.result.nodeType ?? ''))
const shouldShowFallbackCover = computed(() => isBook.value || isJournal.value)
const { coverFallbackImage, isCoverImageFailed, shouldRenderCoverImage, handleCoverImageError } =
  useCoverImageFallback(() => props.result.image)

watch(
  () => props.result.id,
  () => {
    isCoverImageFailed.value = false
  },
)

const metaLine = computed(() => {
  return getSearchResultTitleContext(props.result, [journalEditionInfo.value]).join(' \u2013 ')
})

function handleResultClick() {
  emit('clickResult', props.result)
}
</script>

<template>
  <NuxtLink
    :to="resolveSearchResult(result, hasLicense)"
    :aria-label="getSearchResultAccessibleLabel(result)"
    class="group flex w-full items-center p-s md:p-l gap-s rounded-2xl no-underline bg-surface-primary hover:bg-surface-hover focus:border-s focus:border-border-primary transition-all duration-300 ease-in-out"
    @click="handleResultClick"
  >
    <div class="flex-1 flex flex-col gap-3xs">
      <div class="flex flex-col gap-3xs items-start self-stretch">
        <div class="flex flex-wrap items-center gap-2xs">
          <Icon v-if="!hasLicense" :icon="faLockKeyhole" />
          <span
            class="text-small font-bold global-search-result"
            v-text="result.highlightedTitle"
          />
        </div>
        <span v-if="metaLine" class="text-caption text-secondary">
          {{ metaLine }}
        </span>
      </div>
      <div class="flex flex-col gap-s">
        <p class="global-search-result" v-html="truncatedBodyText" />
        <div class="flex gap-2xs items-start sm:items-center flex-col sm:flex-row">
          <Chip
            size="small"
            variant="filled"
            color="carbon"
            class="group-hover:bg-blurple-200 transition-bg duration-300 ease-in-out"
          >
            {{ nodeTypeToLabels(result.nodeType) }}
          </Chip>
          <template v-if="result.author">
            <Divider orientation="vertical" class="mx-s hidden sm:flex" />
            <span
              class="text-caption truncate sm:max-w-[250px] lg:max-w-[300px] xl:max-w-[339px] block"
              >{{ result.author }}</span
            >
          </template>
          <template v-if="result.editionNumber">
            <Divider orientation="vertical" class="mx-s hidden sm:flex" />
            <span class="text-caption shrink-0">{{ result.editionNumber }}. utgave</span>
          </template>
          <template v-if="formattedDate">
            <Divider orientation="vertical" class="mx-s hidden sm:flex" />
            <span class="text-caption shrink-0">{{ formattedDate }}</span>
          </template>
        </div>
      </div>
    </div>

    <div v-if="result.image || shouldShowFallbackCover" class="hidden md:flex max-w-[96px] h-24">
      <img
        v-if="shouldRenderCoverImage"
        :src="result.image"
        alt=""
        class="w-full h-full"
        @error="handleCoverImageError"
      />
      <img
        v-else-if="shouldShowFallbackCover"
        :src="coverFallbackImage"
        alt="Omslag"
        class="w-full h-full"
      />
    </div>
  </NuxtLink>
</template>
